import { useState, useMemo } from 'react';
import AppLayout from '@/components/layout/AppLayout';
import { Badge } from '@/components/ui/badge';
import {
  Video, Plus, Clock, Users, X, ChevronDown, Copy, LogIn,
  Calendar, CheckCircle2, AlertTriangle, User, Phone,
  Send, Link2,
} from 'lucide-react';

/* ═══ MOCK ═══ */
const TELECONSULTI = [
  { id: 1, ora: '09:00 — 09:30', paziente: 'Rossi Anna', motivo: 'Follow-up parametri respiratori', stato: 'concluso', partecipanti: ['Dott. R. Marino (UVMD)', 'Inf. L. Conti (CM)', 'Caregiver'], link: 'meet.adi.lazio.it/room-a1b2' },
  { id: 2, ora: '10:30 — 11:00', paziente: 'Verdi Luca', motivo: 'Rivalutazione PAI congiunta', stato: 'in-corso', partecipanti: ['Dott.ssa F. Lombardi (UVMD)', 'Dott. F. Bianchi (MMG)', 'Inf. M. Ferrara (CM)'], link: 'meet.adi.lazio.it/room-c3d4' },
  { id: 3, ora: '11:30 — 12:00', paziente: 'Neri Giuseppe', motivo: 'Consulto ortopedico post-chirurgico', stato: 'in-corso', partecipanti: ['Dott. A. De Santis (UVMD)', 'Fisiatra — Dott. E. Tosi', 'Caregiver'], link: 'meet.adi.lazio.it/room-e5f6' },
  { id: 4, ora: '14:00 — 14:30', paziente: 'Ferrara Elena', motivo: 'Coordinamento cure palliative', stato: 'in-attesa', partecipanti: ['Dott.ssa P. Greco (UVMD)', 'Dott. L. Marchetti (MMG)', 'Inf. A. Romano (CM)', 'Psicologo'], link: 'meet.adi.lazio.it/room-g7h8' },
  { id: 5, ora: '15:00 — 15:30', paziente: 'Colombo Maria', motivo: 'Valutazione cognitiva a distanza', stato: 'in-attesa', partecipanti: ['Dott. R. Marino (UVMD)', 'Neuropsicologo', 'Caregiver'], link: 'meet.adi.lazio.it/room-i9j0' },
  { id: 6, ora: '16:00 — 16:30', paziente: 'Esposito Luca', motivo: 'Follow-up nefrologico', stato: 'in-attesa', partecipanti: ['Dott.ssa F. Lombardi (UVMD)', 'Dott.ssa A. Romano (MMG)'], link: 'meet.adi.lazio.it/room-k1l2' },
  { id: 7, ora: '16:30 — 17:00', paziente: 'Bianchi Mario', motivo: 'Prima valutazione post-dimissione', stato: 'in-attesa', partecipanti: ['Dott. A. De Santis (UVMD)', 'Inf. G. Neri (CM)', 'Caregiver'], link: 'meet.adi.lazio.it/room-m3n4' },
  { id: 8, ora: '17:00 — 17:30', paziente: 'Mancini Rosa', motivo: 'Supporto palliativo e caregiver', stato: 'in-attesa', partecipanti: ['Dott.ssa P. Greco (UVMD)', 'Psicologo — Dott. C. Marini', 'Caregiver'], link: 'meet.adi.lazio.it/room-o5p6' },
];

const PROSSIMI = [
  { id: 101, data: '17/04/2026', ora: '10:00 — 10:30', paziente: 'Romano Pietro', motivo: 'Controllo piede diabetico', stato: 'in-attesa', partecipanti: ['Dott.ssa F. Lombardi (UVMD)', 'Inf. L. Conti (CM)'], link: 'meet.adi.lazio.it/room-q7r8' },
  { id: 102, data: '18/04/2026', ora: '11:00 — 11:30', paziente: 'Mancini Bruno', motivo: 'Revisione terapia anticoagulante', stato: 'riprogrammare', partecipanti: ['Dott. R. Marino (UVMD)', 'Dott. G. Conti (MMG)'], link: 'meet.adi.lazio.it/room-s9t0' },
];

const STATO_CONFIG = {
  'in-attesa': { label: 'In Attesa', bg: 'bg-slate-100 dark:bg-slate-800', text: 'text-slate-600 dark:text-slate-300', dot: 'bg-slate-400' },
  'in-corso': { label: 'In Corso', bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-400', dot: 'bg-emerald-500 animate-pulse' },
  'concluso': { label: 'Concluso', bg: 'bg-blue-50 dark:bg-blue-900/20', text: 'text-blue-600 dark:text-blue-400', dot: 'bg-blue-400' },
  'riprogrammare': { label: 'Da Riprogrammare', bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400', dot: 'bg-amber-500' },
};

const MOTIVAZIONI = ['Rivalutazione PAI congiunta', 'Consulto Specialistico', 'Follow-up clinico', 'Prima valutazione post-dimissione', 'Supporto palliativo', 'Coordinamento équipe'];
const PAZIENTI_MOCK = ['Rossi Anna', 'Verdi Luca', 'Neri Giuseppe', 'Ferrara Elena', 'Colombo Maria', 'Esposito Luca', 'Bianchi Mario', 'Mancini Rosa', 'Romano Pietro'];

function TeleconsultoCard({ t, showDate }) {
  const sc = STATO_CONFIG[t.stato] || STATO_CONFIG['in-attesa'];
  const isLive = t.stato === 'in-corso';
  return (
    <div className={`bg-white dark:bg-slate-900 rounded-xl border shadow-sm p-5 ${isLive ? 'border-emerald-300 dark:border-emerald-700 ring-1 ring-emerald-200 dark:ring-emerald-800/40' : 'border-slate-200 dark:border-slate-800'}`} data-testid={`teleconsulto-card-${t.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-xs font-bold text-slate-800 dark:text-white">{showDate && t.data ? `${t.data} · ` : ''}{t.ora}</span>
            <Badge className={`${sc.bg} ${sc.text} border-0 text-[9px] font-bold gap-1`}>
              <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
              {sc.label}
            </Badge>
            {isLive && <Badge className="bg-emerald-500 text-white border-0 text-[9px] font-bold animate-pulse">LIVE</Badge>}
          </div>
          <p className="text-sm font-semibold text-slate-800 dark:text-white mb-0.5">{t.paziente}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">{t.motivo}</p>
          <div className="flex flex-wrap gap-1.5">
            {t.partecipanti.map((p, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-100 dark:bg-slate-800 text-[10px] font-medium text-slate-600 dark:text-slate-300">
                <User className="w-2.5 h-2.5" />{p}
              </span>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <button onClick={() => navigator.clipboard?.writeText(`https://${t.link}`)} className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 text-xs font-medium rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors" data-testid={`copy-link-${t.id}`}>
            <Copy className="w-3 h-3" /> Copia Link
          </button>
          <button className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${isLive ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`} data-testid={`gestisci-${t.id}`}>
            <LogIn className="w-3 h-3" /> {isLive ? 'Entra' : 'Gestisci'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function HubTeleconsulti() {
  const [activeTab, setActiveTab] = useState('oggi');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [formPaziente, setFormPaziente] = useState('');
  const [formData, setFormData] = useState('');
  const [formOra, setFormOra] = useState('');
  const [formMotivazione, setFormMotivazione] = useState('');
  const [invUvmd, setInvUvmd] = useState(true);
  const [invCm, setInvCm] = useState(true);
  const [invMmg, setInvMmg] = useState(false);
  const [invCaregiver, setInvCaregiver] = useState('');

  const countOggi = TELECONSULTI.length;
  const countLive = TELECONSULTI.filter(t => t.stato === 'in-corso').length;
  const countRiprogrammare = PROSSIMI.filter(t => t.stato === 'riprogrammare').length;

  const generatedLink = formPaziente ? `meet.adi.lazio.it/room-${Math.random().toString(36).substring(2, 6)}` : '';

  return (
    <AppLayout role="segreteria">
      <div className="p-4 md:p-6 max-w-[1600px] mx-auto space-y-6" data-testid="hub-teleconsulti-page">

        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Hub Teleconsulti</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Regia e programmazione delle stanze virtuali multidimensionali</p>
          </div>
          <button onClick={() => setDrawerOpen(true)} className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm" data-testid="btn-nuova-stanza">
            <Plus className="w-4 h-4" /> Nuova Stanza Virtuale
          </button>
        </div>

        {/* KPI */}
        <div className="grid grid-cols-3 gap-4" data-testid="teleconsulti-kpi">
          <div className="bg-blue-50 dark:bg-blue-950/20 rounded-2xl border border-blue-200 dark:border-blue-800/40 p-5">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold text-blue-500 uppercase tracking-wider">In Programma Oggi</span>
              <Calendar className="w-5 h-5 text-blue-400" />
            </div>
            <p className="text-3xl font-bold text-blue-700 dark:text-blue-400">{countOggi}</p>
            <p className="text-[10px] text-blue-500 dark:text-blue-400/70 mt-0.5">Sessioni programmate per la giornata</p>
          </div>
          <div className="bg-emerald-50 dark:bg-emerald-950/20 rounded-2xl border border-emerald-200 dark:border-emerald-800/40 p-5">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold text-emerald-500 uppercase tracking-wider">Stanze Attive Ora</span>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <Video className="w-5 h-5 text-emerald-400" />
              </div>
            </div>
            <p className="text-3xl font-bold text-emerald-700 dark:text-emerald-400">{countLive}</p>
            <p className="text-[10px] text-emerald-500 dark:text-emerald-400/70 mt-0.5">Teleconsulti in corso adesso</p>
          </div>
          <div className="bg-amber-50 dark:bg-amber-950/20 rounded-2xl border border-amber-200 dark:border-amber-800/40 p-5">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold text-amber-500 uppercase tracking-wider">Da Riprogrammare</span>
              <AlertTriangle className="w-5 h-5 text-amber-400" />
            </div>
            <p className="text-3xl font-bold text-amber-700 dark:text-amber-400">{countRiprogrammare}</p>
            <p className="text-[10px] text-amber-500 dark:text-amber-400/70 mt-0.5">Sessioni da ricollocare in agenda</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 p-1 bg-slate-100 dark:bg-slate-800 rounded-xl w-fit" data-testid="teleconsulti-tabs">
          {[
            { id: 'oggi', label: 'Oggi', count: countOggi },
            { id: 'prossimi', label: 'Prossimi', count: PROSSIMI.length },
            { id: 'storico', label: 'Storico', count: null },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all ${activeTab === tab.id ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-white shadow-sm' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'}`} data-testid={`tab-${tab.id}`}>
              {tab.label} {tab.count !== null && <span className="ml-1 text-[10px] opacity-60">({tab.count})</span>}
            </button>
          ))}
        </div>

        {/* Content */}
        {activeTab === 'oggi' && (
          <div className="flex flex-col gap-4" data-testid="lista-oggi">
            {TELECONSULTI.map(t => <TeleconsultoCard key={t.id} t={t} />)}
          </div>
        )}

        {activeTab === 'prossimi' && (
          <div className="flex flex-col gap-4" data-testid="lista-prossimi">
            {PROSSIMI.map(t => <TeleconsultoCard key={t.id} t={t} showDate />)}
          </div>
        )}

        {activeTab === 'storico' && (
          <div className="flex items-center justify-center h-48 bg-white dark:bg-slate-900 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700">
            <p className="text-sm text-slate-400">Archivio sessioni passate — Prossimamente</p>
          </div>
        )}
      </div>

      {/* ══ DRAWER: CREAZIONE STANZA ══ */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50" data-testid="drawer-nuova-stanza">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setDrawerOpen(false)} />
          <div className="absolute top-0 right-0 h-full w-full max-w-[550px] bg-white dark:bg-slate-900 shadow-2xl border-l border-slate-200 dark:border-slate-800 flex flex-col">

            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <Video className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h2 className="text-sm font-bold text-slate-800 dark:text-white">Programma Teleconsulto</h2>
                </div>
                <button onClick={() => setDrawerOpen(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" data-testid="drawer-close">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-6">

              {/* Sez 1: Dettagli */}
              <div>
                <h3 className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">Dettagli Sessione</h3>
                <div className="flex flex-col gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Paziente *</label>
                    <div className="relative">
                      <select value={formPaziente} onChange={e => setFormPaziente(e.target.value)} className="w-full h-10 px-3 pr-8 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-200 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500/30" data-testid="select-paziente">
                        <option value="">Seleziona paziente...</option>
                        {PAZIENTI_MOCK.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                      <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Data *</label>
                      <input type="date" value={formData} onChange={e => setFormData(e.target.value)} className="w-full h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/30" data-testid="input-data" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Ora Inizio *</label>
                      <input type="time" value={formOra} onChange={e => setFormOra(e.target.value)} className="w-full h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500/30" data-testid="input-ora" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Motivazione</label>
                    <div className="relative">
                      <select value={formMotivazione} onChange={e => setFormMotivazione(e.target.value)} className="w-full h-10 px-3 pr-8 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-200 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500/30" data-testid="select-motivazione">
                        <option value="">Seleziona motivazione...</option>
                        {MOTIVAZIONI.map(m => <option key={m} value={m}>{m}</option>)}
                      </select>
                      <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Sez 2: Partecipanti */}
              <div>
                <h3 className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">Partecipanti & Inviti</h3>
                <div className="flex flex-col gap-2.5">
                  {[
                    { label: 'Medico UVMD Referente', checked: invUvmd, set: setInvUvmd },
                    { label: 'Case Manager', checked: invCm, set: setInvCm },
                    { label: 'MMG del paziente', checked: invMmg, set: setInvMmg },
                  ].map(p => (
                    <label key={p.label} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-all ${p.checked ? 'border-blue-300 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-950/20' : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50'}`} data-testid={`inv-${p.label.substring(0,5).toLowerCase()}`}>
                      <input type="checkbox" checked={p.checked} onChange={e => p.set(e.target.checked)} className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500/30" />
                      <Users className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-700 dark:text-slate-200">{p.label}</span>
                    </label>
                  ))}
                  <div className="mt-2">
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Invia link al Caregiver (SMS / Email)</label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                        <input type="text" value={invCaregiver} onChange={e => setInvCaregiver(e.target.value)} placeholder="+39 xxx xxx xxxx o email@..." className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-200 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30" data-testid="input-caregiver" />
                      </div>
                      <button className="px-3 h-10 rounded-lg border border-slate-300 dark:border-slate-600 text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors flex items-center gap-1.5" data-testid="btn-invia-link">
                        <Send className="w-3.5 h-3.5" /> Invia
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sez 3: Link Stanza */}
              {formPaziente && (
                <div>
                  <h3 className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">Link Stanza Virtuale</h3>
                  <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                    <Link2 className="w-4 h-4 text-blue-500 shrink-0" />
                    <span className="text-sm text-blue-600 dark:text-blue-400 font-mono flex-1 truncate">https://{generatedLink}</span>
                    <button onClick={() => navigator.clipboard?.writeText(`https://${generatedLink}`)} className="p-1.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors" data-testid="copy-generated-link">
                      <Copy className="w-3.5 h-3.5 text-slate-400" />
                    </button>
                  </div>
                  <p className="text-[10px] text-slate-400 mt-1.5">Link generato automaticamente. Verrà inviato ai partecipanti selezionati.</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-800 shrink-0 flex items-center justify-end gap-3">
              <button onClick={() => setDrawerOpen(false)} className="px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 text-xs font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors" data-testid="btn-annulla">
                Annulla
              </button>
              <button
                disabled={!formPaziente || !formData || !formOra}
                className={`px-6 py-2.5 rounded-lg text-sm font-bold transition-all flex items-center gap-2
                  ${formPaziente && formData && formOra
                    ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/20'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 cursor-not-allowed'}`}
                data-testid="btn-salva-inviti"
              >
                <CheckCircle2 className="w-4 h-4" /> Salva e Invia Inviti
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
